import { ClickAwayListener, Grid, Popover, Theme, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import moment from "moment-timezone";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { TimeDisplay } from "src/components/TimeDisplay";
import { setWindowTimezone } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { defaultCrewBase, defaultTimezone } from "src/redux/reducers/CrewScheduleReducer";
import { IAirportOption, IStructuresState } from "src/redux/reducers/StructuresReducer";
import { updateAirportOptions } from "src/utils/airportHelper";
import { BaseTimelineGL } from "../../SharedGL/BaseTimelineGL";

interface ITimelineTimeProps {
  container?: HTMLDivElement;
  setFocusOnDate?: (date?: Date) => void;
  allowTimezoneControl?: boolean;
  windowId: string;
}

const timelineTimeStyles = (theme: Theme, crewFilterTimezoneMatch: boolean): any => ({
  height: BaseTimelineGL.timelineStart,
  width: 60,
  "& .time": {
    textAlign: "right",
    fontSize: 24,
    lineHeight: "32px",
    height: 32,
    cursor: "pointer",
    color: theme.custom.timeline.webglCanvas.clock,
  },
  "& .timezone": {
    textAlign: "right",
    fontSize: 12,
    fontWeight: "bold",
    cursor: "pointer",
    color: crewFilterTimezoneMatch ? theme.custom.timeline.webglCanvas.clock : theme.palette.error.main,
    whiteSpace: "nowrap",
  },
});

const clockPopoverStyles = (theme: Theme): any => ({
  backgroundColor: "transparent",
  boxShadow: "none",
  zIndex: 2000,
  maxHeight: "800px",
  maxWidth: "600px",
  "& .select_timezone_menu": {
    margin: "4px 2px",
    backgroundColor: alpha(theme.custom.contextPopover.backgroundColor, 0.95),
    color: theme.custom.contextPopover.foreGroundColor,
    boxShadow: theme.shadows[5],
    borderRadius: "5px",
    padding: "10px",
    "& .time": { fontSize: 32, lineHeight: "32px" },
    "& .info": { fontSize: 12, textAlign: "right", marginRight: "5px" },
    "& .timezone_selector": {
      margin: "0px 0px 0px 8px",
      width: "300px",
      "& label": { textAlign: "center", margin: "2px 0px auto auto" },
    },
  },
});

interface IAnchorPos {
  left: number;
  top: number;
}

export const TimelineTime = (props: ITimelineTimeProps): JSX.Element => {
  const { container, setFocusOnDate, allowTimezoneControl, windowId } = props;
  const theme: Theme = useTheme();
  const dispatch = useDispatch();

  const [anchorPos, setAnchorPos] = useState<IAnchorPos>({ left: 0, top: 0 });
  const [anchorEl, setAnchorEl] = useState(null);

  const windowProperties = useSelector((state: IRootState) => state.crewSchedule.windowProperties[windowId]);
  const crewBase = windowProperties?.crewBase ?? defaultCrewBase;
  const displayTimezone = windowProperties?.displayTimezone ?? defaultTimezone;

  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { airportOptions } = structures;

  // TRUE is crewfilter is default or matches timezone selected in clock.
  const crewFilterTimezoneMatch = useMemo(() => {
    return !crewBase || crewBase?.key === "All" || displayTimezone?.ident === crewBase?.key;
  }, [displayTimezone, crewBase]);

  // If Crew Base is selected and has available timezone, set display timezone.
  useEffect(() => {
    if (!crewBase || crewBase.key === "All") {
      return;
    }
    // Only switch timezone if selected crewBase and displayed timezone don't match.
    if (crewBase?.key) {
      // Search for an airport and set's it's timezone.
      const updateAirports = async (): Promise<any> => {
        const results = await updateAirportOptions(crewBase.key, airportOptions, dispatch);
        const airportResult: IAirportOption = Array.isArray(results) ? results[0] : null;
        if (!!airportResult && airportResult.ident === crewBase.key && airportResult.timezoneId) {
          dispatch(setWindowTimezone(windowId, airportResult));
        }
      };
      // If the airport option already exists use it. Otherwise search and add it.
      if (airportOptions[crewBase.key]) {
        dispatch(setWindowTimezone(windowId, airportOptions[crewBase.key]));
      } else {
        updateAirports();
      }
    }
  }, [airportOptions, crewBase, dispatch, windowId]); //displayTimezone

  const timezoneOptions = useMemo(() => {
    const options = Object.values(airportOptions);
    options.unshift(defaultTimezone);
    return options;
  }, [airportOptions]);

  const timezoneLabel = useMemo(() => {
    const date = new Date();
    const zoneAbbr = displayTimezone?.iata ? moment.tz(date, displayTimezone.timezoneId).zoneAbbr() : "UTC";
    const iata = displayTimezone?.iata ? `(${displayTimezone.iata})` : "";
    return displayTimezone.name === "UTC" ? displayTimezone.name : `${zoneAbbr} ${iata}`;
  }, [displayTimezone]);

  const handleClickTime = (): void => {
    if (setFocusOnDate && typeof setFocusOnDate === "function") {
      setFocusOnDate(new Date());
    }
  };

  const handleClickTimezone = (event: any): void => {
    event.persist();
    setAnchorEl(event.currentTarget);
    if (container) {
      const { top, left } = container.getBoundingClientRect();
      setAnchorPos({ top, left });
    }
  };

  const handleSelectTimezone = (displayTimezone: IAirportOption): void => {
    dispatch(setWindowTimezone(windowId, displayTimezone));
    setAnchorEl(null);
  };

  return (
    <>
      <Grid sx={{ ...timelineTimeStyles(theme, crewFilterTimezoneMatch), display: "flex" }} item>
        <Grid container direction="column" justifyContent="center">
          <Grid item>
            <Tooltip title="Focus Current Day" enterDelay={1000} placement="top">
              <div onClick={() => handleClickTime()} className="time">
                <TimeDisplay allowTimezoneControl={allowTimezoneControl} displayTimezone={displayTimezone} />
              </div>
            </Tooltip>
            {container && allowTimezoneControl ? (
              <Tooltip
                title={crewFilterTimezoneMatch ? "Change Timezone" : "Timezone mismatch with selected Base filter."}
                enterDelay={1000}
                placement="top"
              >
                <div className="timezone" onClick={handleClickTimezone}>
                  {timezoneLabel}
                </div>
              </Tooltip>
            ) : (
              <div className="timezone">UTC</div>
            )}
          </Grid>
        </Grid>
      </Grid>
      {!!anchorEl && (
        <ClickAwayListener onClickAway={() => anchorEl && setAnchorEl(null)}>
          <Popover
            style={{ position: "inherit" }}
            anchorEl={anchorEl}
            anchorReference="anchorPosition"
            anchorPosition={{ top: anchorPos.top + 35, left: anchorPos.left }}
            open={!!anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "center", horizontal: "left" }}
            transformOrigin={{ vertical: "center", horizontal: "left" }}
            disableScrollLock={true}
            slotProps={{
              paper: {
                sx: clockPopoverStyles(theme),
              },
            }}
          >
            <div className="select_timezone_menu">
              <Grid container justifyContent="space-around" alignItems="center">
                <div className="time">
                  <TimeDisplay allowTimezoneControl={allowTimezoneControl} displayTimezone={displayTimezone} />
                </div>
                <div className="info"> Search for airport(IATA) or timezone </div>
              </Grid>
              <SelectField
                className="timezone_selector"
                label={timezoneLabel}
                name="timezone"
                onInputChange={(ident: string) => {
                  updateAirportOptions(ident, airportOptions, dispatch);
                }}
                value={displayTimezone}
                onChange={handleSelectTimezone}
                options={timezoneOptions}
                getOptionLabel={(airport: IAirportOption) => {
                  const iata = airport?.iata ? `(${airport.iata})` : "";
                  return `${airport.timezoneId} ${iata}`;
                }}
                getOptionValue={(airport: IAirportOption) => airport.ident}
                // menuPortalTarget={mosaicPane}
                menuPosition="fixed"
                isSearchable={true}
                placeholder="Search Airport/Timezone..."
                noOptionsMessage={() => "Search Airport or Timezone..."}
                openMenuOnFocus={false}
                isClearable={false}
              />
            </div>
          </Popover>
        </ClickAwayListener>
      )}
    </>
  );
};
